import clsx from "clsx";
import {
  memo,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { Button } from "react-aria-components";
import type {
  Definition,
  Nodes,
  Parent,
  Root,
  TableCell,
  TableRow,
} from "mdast";

import externalLinkIcon from "@/assets/figma/chat-context-bar/external-link.svg";

import { safeMarkdownLink } from "./markdownLinkPolicy";
import { splitMerryHighlightText } from "./markdownProjection";
import { registerMerryMarkdownProjection } from "./markdownProjectionRegistry";
import { useMerryMarkdownDocument } from "./useMerryMarkdownDocument";
import type {
  MerryMarkdownAction,
  MerryMarkdownDocument,
  MerryMarkdownProjectionLeaf,
} from "./markdownSchema";

export interface MerryMarkdownSelectableIdentity {
  contentHash: string;
  messageId: string;
  sequence: number;
}

interface MerryMarkdownViewProps {
  actions?: ReadonlyArray<MerryMarkdownAction>;
  className?: string;
  density?: "compact" | "default";
  document: MerryMarkdownDocument;
  onAction?: (actionId: string) => void;
  selectable?: MerryMarkdownSelectableIdentity;
}

interface MerryMarkdownProps extends Omit<MerryMarkdownViewProps, "document"> {
  cacheKey?: string | null;
  isStreaming?: boolean;
  source: string;
}

interface RenderContext {
  definitions: ReadonlyMap<string, Definition>;
  leaves: ReadonlyMap<string, MerryMarkdownProjectionLeaf>;
  onAction?: (actionId: string) => void;
}

export const MerryMarkdown = memo(function MerryMarkdown({
  actions,
  cacheKey,
  className,
  density,
  isStreaming,
  onAction,
  selectable,
  source,
}: MerryMarkdownProps) {
  const document = useMerryMarkdownDocument({ cacheKey, isStreaming, source });
  return (
    <MerryMarkdownDocumentView
      actions={actions}
      className={className}
      density={density}
      document={document}
      onAction={onAction}
      selectable={selectable}
    />
  );
});

export const MerryMarkdownDocumentView = memo(
  function MerryMarkdownDocumentView({
    actions = [],
    className,
    density = "default",
    document,
    onAction,
    selectable,
  }: MerryMarkdownViewProps) {
    const leaves = useMemo(
      () => new Map(document.projection.leaves.map((leaf) => [leaf.id, leaf])),
      [document],
    );
    const definitions = useMemo(
      () => collectDefinitions(document.root),
      [document.root],
    );
    const context = useMemo<RenderContext>(
      () => ({ definitions, leaves, onAction }),
      [definitions, leaves, onAction],
    );
    const registerRoot = useCallback(
      (element: HTMLDivElement | null) => {
        if (element) {
          registerMerryMarkdownProjection(element, document.projection);
        }
      },
      [document.projection],
    );

    return (
      <div
        {...(selectable && !document.pending
          ? {
              "data-assistant-selectable": "",
              "data-content-hash": selectable.contentHash,
              "data-message-id": selectable.messageId,
              "data-sequence": selectable.sequence,
            }
          : {})}
        className={clsx("merry-markdown", className)}
        data-density={density}
        data-fallback-reason={document.fallbackReason ?? undefined}
        data-markdown-pending={document.pending ? "" : undefined}
        data-md-projection-root=""
        ref={registerRoot}
      >
        <MarkdownChildren context={context} node={document.root} path="root" />
        {actions.length > 0 ? (
          <div className="merry-markdown__actions">
            {actions.map((action) => (
              <Button
                className="merry-markdown__action"
                isDisabled={!onAction}
                key={action.id}
                onPress={() => onAction?.(action.id)}
              >
                <span aria-hidden="true" className="merry-markdown__action-plus">
                  +
                </span>
                <span>{action.label}</span>
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);

function collectDefinitions(root: Root) {
  const definitions = new Map<string, Definition>();
  for (const child of root.children) {
    if (child.type === "definition") {
      definitions.set(child.identifier.toLowerCase(), child);
    }
  }
  return definitions;
}

function MarkdownChildren({
  context,
  node,
  path,
}: {
  context: RenderContext;
  node: Parent;
  path: string;
}) {
  return node.children.map((child, index) => (
    <MarkdownNode
      context={context}
      key={`${path}.${index}`}
      node={child as Nodes}
      path={`${path}.${index}`}
    />
  ));
}

function MarkdownLeaf({
  className,
  context,
  id,
  text,
}: {
  className?: string;
  context: RenderContext;
  id: string;
  text: string;
}) {
  const leaf = context.leaves.get(id);
  return (
    <span
      className={className}
      data-md-leaf-id={id}
      data-projection-end={leaf?.endOffsetUtf16}
      data-projection-start={leaf?.startOffsetUtf16}
    >
      {text}
    </span>
  );
}

function MarkdownText({
  context,
  path,
  value,
}: {
  context: RenderContext;
  path: string;
  value: string;
}) {
  return splitMerryHighlightText(value, path).map((segment) => (
    <MarkdownLeaf
      className={segment.highlighted ? "merry-markdown__highlight" : undefined}
      context={context}
      id={segment.id}
      key={segment.id}
      text={segment.text}
    />
  ));
}

function MarkdownLink({
  children,
  context,
  path,
  title,
  url,
}: {
  children: Parent;
  context: RenderContext;
  path: string;
  title: string | null | undefined;
  url: string;
}) {
  const safeLink = safeMarkdownLink(url);
  const content = (
    <>
      <MarkdownChildren context={context} node={children} path={path} />
      {safeLink?.external ? (
        <img alt="" aria-hidden="true" src={externalLinkIcon} />
      ) : null}
    </>
  );
  if (!safeLink) {
    return <span className="merry-markdown__link merry-markdown__link--invalid">{content}</span>;
  }
  return (
    <a
      className="merry-markdown__link"
      href={safeLink.href}
      rel={safeLink.external ? "noopener noreferrer" : undefined}
      target={safeLink.external ? "_blank" : undefined}
      title={title ?? undefined}
    >
      {content}
    </a>
  );
}

function MarkdownTable({
  context,
  node,
  path,
}: {
  context: RenderContext;
  node: Extract<Nodes, { type: "table" }>;
  path: string;
}) {
  const [head, ...body] = node.children;
  return (
    <div className="merry-markdown__table-wrap">
      <table>
        {head ? (
          <thead>
            <MarkdownTableRow context={context} head node={head} path={`${path}.0`} />
          </thead>
        ) : null}
        {body.length > 0 ? (
          <tbody>
            {body.map((row, index) => (
              <MarkdownTableRow
                context={context}
                head={false}
                key={`${path}.${index + 1}`}
                node={row}
                path={`${path}.${index + 1}`}
              />
            ))}
          </tbody>
        ) : null}
      </table>
    </div>
  );
}

function MarkdownTableRow({
  context,
  head,
  node,
  path,
}: {
  context: RenderContext;
  head: boolean;
  node: TableRow;
  path: string;
}) {
  return (
    <tr>
      {node.children.map((cell, index) => (
        <MarkdownTableCell
          context={context}
          head={head}
          key={`${path}.${index}`}
          node={cell}
          path={`${path}.${index}`}
        />
      ))}
    </tr>
  );
}

function MarkdownTableCell({
  context,
  head,
  node,
  path,
}: {
  context: RenderContext;
  head: boolean;
  node: TableCell;
  path: string;
}) {
  const children = <MarkdownChildren context={context} node={node} path={path} />;
  return head ? <th>{children}</th> : <td>{children}</td>;
}

function MarkdownNode({
  context,
  node,
  path,
}: {
  context: RenderContext;
  node: Nodes;
  path: string;
}): ReactNode {
  switch (node.type) {
    case "root":
      return <MarkdownChildren context={context} node={node} path={path} />;
    case "heading": {
      const children = <MarkdownChildren context={context} node={node} path={path} />;
      if (node.depth === 1) return <h1>{children}</h1>;
      if (node.depth === 2) return <h2>{children}</h2>;
      if (node.depth === 3) return <h3>{children}</h3>;
      return (
        <p aria-level={node.depth} className="merry-markdown__minor-heading" role="heading">
          {children}
        </p>
      );
    }
    case "paragraph":
      return <p><MarkdownChildren context={context} node={node} path={path} /></p>;
    case "text":
      return <MarkdownText context={context} path={path} value={node.value} />;
    case "strong":
      return <strong><MarkdownChildren context={context} node={node} path={path} /></strong>;
    case "emphasis":
      return <em><MarkdownChildren context={context} node={node} path={path} /></em>;
    case "delete":
      return <del><MarkdownChildren context={context} node={node} path={path} /></del>;
    case "inlineCode":
      return (
        <code className="merry-markdown__inline-code">
          <MarkdownLeaf context={context} id={`${path}:inline-code`} text={node.value} />
        </code>
      );
    case "code":
      return (
        <pre className="merry-markdown__code-block">
          <code>
            <MarkdownLeaf context={context} id={`${path}:code`} text={node.value} />
          </code>
        </pre>
      );
    case "link":
      return <MarkdownLink children={node} context={context} path={path} title={node.title} url={node.url} />;
    case "linkReference": {
      const definition = context.definitions.get(node.identifier.toLowerCase());
      return definition ? (
        <MarkdownLink children={node} context={context} path={path} title={definition.title} url={definition.url} />
      ) : (
        <span className="merry-markdown__link merry-markdown__link--invalid">
          <MarkdownChildren context={context} node={node} path={path} />
        </span>
      );
    }
    case "blockquote": {
      const isNote = Reflect.get(node.data ?? {}, "merryNote") === true;
      return (
        <blockquote className={isNote ? "merry-markdown__note" : undefined}>
          <MarkdownChildren context={context} node={node} path={path} />
        </blockquote>
      );
    }
    case "list": {
      const children = <MarkdownChildren context={context} node={node} path={path} />;
      return node.ordered ? <ol start={node.start ?? undefined}>{children}</ol> : <ul>{children}</ul>;
    }
    case "listItem":
      return (
        <li data-checked={node.checked ?? undefined}>
          <MarkdownChildren context={context} node={node} path={path} />
        </li>
      );
    case "break":
      return <br />;
    case "thematicBreak":
      return <hr />;
    case "html":
      return <MarkdownLeaf className="merry-markdown__raw-html" context={context} id={`${path}:html`} text={node.value} />;
    case "image":
    case "imageReference": {
      const label = node.alt?.trim() || "未命名图片";
      const text = `[图片：${label}]`;
      return <MarkdownLeaf className="merry-markdown__image-fallback" context={context} id={`${path}:image`} text={text} />;
    }
    case "table":
      return <MarkdownTable context={context} node={node} path={path} />;
    case "tableRow":
      return <MarkdownTableRow context={context} head={false} node={node} path={path} />;
    case "tableCell":
      return <MarkdownTableCell context={context} head={false} node={node} path={path} />;
    case "footnoteReference": {
      const text = `[${node.label ?? node.identifier}]`;
      return <MarkdownLeaf className="merry-markdown__footnote-ref" context={context} id={`${path}:footnote`} text={text} />;
    }
    case "footnoteDefinition":
      return (
        <aside className="merry-markdown__footnote">
          <MarkdownChildren context={context} node={node} path={path} />
        </aside>
      );
    case "definition":
    case "yaml":
      return null;
  }
}
